import React, { useEffect } from 'react'
import BEMHelper from '@ynap/bem-helper'

import { Button } from '@ynap/button'
import { Link } from 'react-router-dom'
import Notification from '@ynap/notification'
import DeliveryTime from '../../OrderHistory/OrderTracking/DeliveryTime/DeliveryTime'
import Timeline from '../../OrderHistory/OrderTracking/Timeline/Timeline'
import PrimaryStatus from '../../OrderHistory/OrderTracking/PrimaryStatus/PrimaryStatus'
import OrderStatusSkeleton from '../OrderStatusSkeleton'

import { Return, OrderItem, Exchange } from '../../../utils/order-status/interfaces'
import useOrderTracking from '../../../hooks/useOrderTracking'

import track from 'react-tracking'
import { trackOrderOrReturnCTA, trackBookCollectionCTA } from '../analytics'
import { trackOrderDetailsTracking } from '../../OrderHistory/OrderTracking/analytics'
import ReturnLabelButton from './ReturnLabelButton/ReturnLabelButton'

const bem = new BEMHelper('OrderInformation')

export type OrderDetailsStatusProps = {
    externalOrderId: string
    isOrderDetailsFetched: boolean
    activeOrderStatus: string
    orderDateRaw: string
    timeDeliveredRaw: string
    dateShippedRaw: string
    messages: any
    config: any
    shippingAddress: {
        country: string
    }
    tracking: {
        getTrackingData: () => void
        trackEvent: (event: any) => void
    }
    shippingMethod: Array<string>
    locale: {
        country: string
        currency: string
        id: string
        langISO: string
        language: string
    }
    guestUser: boolean
    returns: Array<Return>
    orderItems: Array<OrderItem>
    exchanges: Array<Exchange>
    isPartialReturnRefused: boolean
    isPartialReturn: boolean
    isOrderCancelled: boolean
    isReturnRefused: boolean
    urlPath: string
    courierTrackingId: string
    bookReturnEnabledInCountry: boolean
    returnLabelSuccess,
    returnLabelError,
    isFetchingReturnLabel,
    fetchReturnLabel,
    clearReturnLabelState,
    email,
    isReturnable: string
}

const bookReturnCTAStatuses = ['RETURN_REQUESTED', 'EXCHANGE_REQUESTED']

const OrderDetailsStatus: React.FC<OrderDetailsStatusProps> = ({
    isOrderDetailsFetched,
    messages,
    locale,
    urlPath,
    externalOrderId,
    isOrderCancelled,
    isReturnRefused,
    activeOrderStatus,
    courierTrackingId,
    returns,
    exchanges,
    orderItems,
    orderDateRaw,
    timeDeliveredRaw,
    dateShippedRaw,
    shippingAddress,
    config,
    shippingMethod,
    isPartialReturnRefused,
    isPartialReturn,
    tracking,
    bookReturnEnabledInCountry,
    returnLabelSuccess,
    returnLabelError,
    isFetchingReturnLabel,
    fetchReturnLabel,
    clearReturnLabelState,
    email,
    isReturnable,
}) => {
    const { aftershipMCMNotifiedCountries = [], flags = {} } = config?.head ?? {}
    const { enableReturnLabelBtnCountryList = [] } = flags
    const { country = '' } = locale

    const isOrderReturnable = isReturnable === 'true' || activeOrderStatus === 'RETURN_REQUESTED'

    const isReturnOrExchange = returns?.length > 0 || exchanges?.length > 0

    const customerCareEmail =
        (typeof window !== 'undefined' && window.SF?.getContactDetails?.()?.customerCare?.email) || ''

    const mcmAftershipNotif = messages?.orderTracking?.mcmAftershipNotif?.({ customerCareEmail })
    const isUserCountryInMCMList = aftershipMCMNotifiedCountries.includes(country.toUpperCase())

    const isReturnLabelEnabledinCountry = 
        country.toUpperCase() === shippingAddress?.country.toUpperCase() && 
        enableReturnLabelBtnCountryList?.includes(shippingAddress?.country.toUpperCase())

    const showMCMAfterShipNotif = isUserCountryInMCMList && !!mcmAftershipNotif

    const newestReturnOrExchangeId = returns?.[returns.length - 1]?.returnIdentifier || exchanges?.[exchanges.length - 1]?.exchangeIdentifier

    const {
        orderPrimaryStatus,
        orderTrackingStatus,
        orderTrackingData,
        orderEstimatedDeliveryDate,
        orderDeliveryDate,
        courier,
        isFinalStatus,
    } = useOrderTracking({
        orderStatus: activeOrderStatus,
        orderId: isReturnOrExchange ? returns?.[0]?.returnIdentifier : externalOrderId,
        trackingNumber: isReturnOrExchange ? orderItems?.[0]?.extnReturnTrackingNo : courierTrackingId,
        orderInformation: {
            timeDelivered: timeDeliveredRaw,
            dateShipped: dateShippedRaw,
            shippingCountry: shippingAddress?.country,
            shippingMethod,
            isReturn: isReturnOrExchange,
            isPartialReturnRefused,
        },
    })

    useEffect(() => {
        const primaryStatus = orderPrimaryStatus.status
        const events = orderTrackingData?.find((obj) => obj.status === primaryStatus)?.events
        isFinalStatus &&
            primaryStatus &&
            tracking?.trackEvent(
                trackOrderDetailsTracking(
                    [
                        {
                            externalOrderId,
                            orderPrimaryStatus: primaryStatus,
                            orderTrackingStatus: primaryStatus !== orderTrackingStatus ? orderTrackingStatus : '',
                            orderDateRaw,
                            orderEstimatedDeliveryDate,
                            courier,
                            currentLocation: events?.[events.length - 1]?.location || '',
                        },
                    ],
                    isReturnOrExchange ? 'return' : 'order'
                )
            )
    }, [isFinalStatus, orderPrimaryStatus.status])

    const showLoadingStatus = !(isOrderDetailsFetched && isFinalStatus)
    const renderComponent = !showLoadingStatus && orderPrimaryStatus?.status

    if (showLoadingStatus) return <OrderStatusSkeleton />
    if (!renderComponent) return null

    const renderPrimaryStatusOnly = isOrderCancelled || isReturnRefused

    const primaryStatusComponent = (
        <PrimaryStatus
            orderPrimaryStatus={orderPrimaryStatus}
            messages={messages}
            withDescription
            noCenter={renderPrimaryStatusOnly}
        />
    )

    const isPremierOrder = shippingMethod?.some(method => method?.toLowerCase().includes('premier'))

    const CTAComponent = bookReturnEnabledInCountry && bookReturnCTAStatuses.includes(orderPrimaryStatus.status) ? (
        <>
            {isOrderReturnable && 
                <Button
                    type="primary"
                    component={Link}
                    to={`/${locale.id}/account/${urlPath}/${externalOrderId}/pickup`}
                    className={bem('bookCollectionButton')}
                    onClick={() => tracking.trackEvent(trackBookCollectionCTA())}
                >
                    {messages.createCollection.bookCollection()}
                </Button>
            }

            {isReturnLabelEnabledinCountry && isOrderReturnable && !isPremierOrder &&
                <ReturnLabelButton
                    className={bem('returnLabelButton')}
                    returnLabelSuccess={returnLabelSuccess}
                    returnLabelError={returnLabelError}
                    isFetchingReturnLabel={isFetchingReturnLabel}
                    fetchReturnLabel={fetchReturnLabel}
                    clearReturnLabelState={clearReturnLabelState}
                    returnOrExchangeId={newestReturnOrExchangeId}
                    externalOrderId={externalOrderId}
                    email={email}
                    locale={locale}
                    messages={messages}
                    isConfirmationPage={false}
                />
            }
            
            <Button
                type="primary-link"
                component={Link}
                to={`/${locale.id}/account/${urlPath}/${externalOrderId}/track`}
                className={bem('trackLink')}
                onClick={() => {
                    tracking.trackEvent(trackOrderOrReturnCTA('return', 'link'))
                }}
            >
                {messages.returnTracking.trackDetails()}
            </Button>
        </>
    ) : (
        <>
            {isReturnLabelEnabledinCountry && isOrderReturnable && !isPremierOrder && isPartialReturn &&
                <ReturnLabelButton
                    className={bem('returnLabelButton')}
                    returnLabelSuccess={returnLabelSuccess}
                    returnLabelError={returnLabelError}
                    isFetchingReturnLabel={isFetchingReturnLabel}
                    fetchReturnLabel={fetchReturnLabel}
                    clearReturnLabelState={clearReturnLabelState}
                    returnOrExchangeId={newestReturnOrExchangeId}
                    externalOrderId={externalOrderId}
                    email={email}
                    locale={locale}
                    messages={messages}
                    isConfirmationPage={false}
                />
            }
            <Button
                type="primary-link"
                component={Link}
                to={`/${locale.id}/account/${urlPath}/${externalOrderId}/track`}
                className={bem('trackLink')}
                onClick={() => {
                    tracking.trackEvent(trackOrderOrReturnCTA(isReturnOrExchange ? 'return' : 'order', 'cta'))
                }}
            >
                {isReturnOrExchange
                    ? messages.returnTracking.trackDetails()
                    : messages.orderTracking.trackDetails()}
            </Button>
        </>
    )

    const timelineAndCTAComponent = (
        <>
            <Timeline orderPrimaryStatus={orderPrimaryStatus} orderTrackingData={orderTrackingData} />
            {showMCMAfterShipNotif && (
                <Notification className={bem('mcmAftershipNotif')} message={mcmAftershipNotif} type="info" />
            )}
            <DeliveryTime
                orderPrimaryStatus={orderPrimaryStatus}
                orderEstimatedDeliveryDate={orderEstimatedDeliveryDate}
                orderDeliveryDate={orderDeliveryDate}
                messages={messages}
                locale={locale}
                type={isReturnOrExchange ? 'return' : 'order'}
            />
            {CTAComponent}
        </>
    )

    return (
        <div className={bem('orderStatus')}>
            {primaryStatusComponent}
            {!renderPrimaryStatusOnly && timelineAndCTAComponent}
        </div>
    )
}

export default track()(OrderDetailsStatus)
